// Standardized collection organization: split a collection's members between the string-based `section`
// ZONES (a reserved column on each item — "header" / "intro" / null=General) and order the dynamic
// join-table CollectionSections that render via SectionRenderer. Both are sorted by `position` (nulls last,
// stable). Extracted from resource-collection-template so every collection surface organizes the same way.

/** Reserved string-section zones handled specially (a featured header item, an intro text block). Anything
 *  else — including a null/blank section — falls into the General bucket. */
export const RESERVED_SECTIONS = ["header", "intro"] as const
export type ReservedSection = (typeof RESERVED_SECTIONS)[number]

const RESERVED = new Set<string>(RESERVED_SECTIONS)

/** Minimal shape the organizer reads off a collection member (a CollectionItem). Items order WITHIN their
 *  section/zone by `sectionOrder` (the collection_items column), not `position`. */
export interface OrganizableItem {
  section?: string | null
  sectionOrder?: number | null
}

/** Minimal shape the organizer reads off a dynamic CollectionSection. Sections order by `position`. */
export interface OrganizableSection {
  position?: number | null
}

export interface OrganizedCollection<
  I extends OrganizableItem,
  S extends OrganizableSection,
> {
  /** Items in the reserved "header" zone (e.g. a featured codex record in the header band). */
  headerItems: I[]
  /** Items in the reserved "intro" zone (e.g. an intro text block above the sections). */
  introItems: I[]
  /** Everything else — no section or a non-reserved section — shown as the flat General list. */
  generalItems: I[]
  /** Dynamic join-table sections, ordered by position. Rendered via SectionRenderer. */
  sections: S[]
  hasSections: boolean
}

// Order ascending by a numeric key with null/undefined LAST. Array.prototype.sort is stable, so equal keys
// (and the all-null tail) keep their incoming order.
const sortedByKey = <T>(
  items: readonly T[],
  key: (item: T) => number | null | undefined
): T[] => [...items].sort((a, b) => (key(a) ?? Infinity) - (key(b) ?? Infinity))

/**
 * Organize a collection's members into the reserved zones + the flat General list, and return the dynamic
 * sections ordered by position. Everything is position-sorted (nulls last, stable) so callers don't
 * re-implement the split/sort per template.
 */
export function organizeCollection<
  I extends OrganizableItem,
  S extends OrganizableSection,
>(collection: {
  ungroupedItems?: I[] | null
  sections?: S[] | null
}): OrganizedCollection<I, S> {
  const ungrouped = collection.ungroupedItems ?? []
  // Items sort by sectionOrder (within their zone); sections sort by position.
  const byItemOrder = (items: I[]) => sortedByKey(items, (i) => i.sectionOrder)
  const sections = sortedByKey(collection.sections ?? [], (s) => s.position)

  const inZone = (item: I, zone: ReservedSection) => item.section === zone
  const isGeneral = (item: I) => !item.section || !RESERVED.has(item.section)

  return {
    headerItems: byItemOrder(ungrouped.filter((i) => inZone(i, "header"))),
    introItems: byItemOrder(ungrouped.filter((i) => inZone(i, "intro"))),
    generalItems: byItemOrder(ungrouped.filter(isGeneral)),
    sections,
    hasSections: sections.length > 0,
  }
}
